import React from 'react';
import { Box, Typography, IconButton, Link } from '@mui/material';
import { TFunction } from 'i18next';
import {
  LinkIcons,
  MapPinIcons,
  MicIcons,
  NotebookIcons,
} from '../../../../../../../icons/icons';
import { ADMIN_PAGES_KEYS } from '../../../../../../../i18n/constants/keys';
import { ContextMenuButton, NameEmailAvatar } from '../../../../../../../ui';
import { ICandidate } from '../../../../../../../utils/types/JobOpenings';
import StatusTracker from '../Components/StatusTracker';

export interface GetColumnsArgs {
  handleOpenMenu: (
    e: React.MouseEvent<HTMLButtonElement>,
    row: ICandidate
  ) => void;
  handleCloseMenu: () => void;
  onRecordInterview?: (row: ICandidate) => void;
  onOpenPanelistNotes?: (row: ICandidate) => void;
  recordingRowId?: string | number | null;
  anchorEl: HTMLElement | null;
  t: TFunction;
}

export const getColumns = ({
  handleOpenMenu,
  handleCloseMenu,
  anchorEl,
  onRecordInterview,
  onOpenPanelistNotes,
  recordingRowId,
  t,
}: GetColumnsArgs) => {
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;
  return [
    {
      key: 'applicant',
      label: t(SI.candidates.columns.applicant),
      width: '150px',
      align: 'left' as const,
      render: (row: ICandidate) => (
        <NameEmailAvatar
          email={row.email}
          name={`${row.firstName} ${row.lastName}`}
          avatar={''}
        />
      ),
    },
    {
      key: 'interviewType',
      label: t(SI.candidates.columns.interviewType),
      width: '130px',
      align: 'left' as const,
      render: (row: ICandidate) => (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: '0.875rem',
            fontWeight: 500,
            bgcolor: 'action.hover',
            borderRadius: '9999px',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              bgcolor: 'background.paper',
              color: 'text.primary',
              borderRadius: '9999px',
            }}
          >
            {row.interviewType}
          </Box>
        </Box>
      ),
      disableSort: true,
    },

    // Link / Address Column
    {
      key: 'linkOrAddress',
      label: t(SI.candidates.columns.linkOrAddress),
      width: '220px',
      align: 'left' as const,
      render: (row: ICandidate) => {
        const isVirtual = row.interviewType.trim().toLowerCase() === 'virtual';
        const raw = isVirtual ? row.meetingLink || '' : '';
        const href =
          raw.match(/^https?:\/\//i) !== null ? raw : raw ? `https://${raw}` : '';

        const text = isVirtual
          ? row.meetingLink || 'No Link'
          : row.inPersonAddress || 'No Address';
        const label = isVirtual ? 'Meeting:' : 'Address:';
        const Icon = isVirtual ? LinkIcons : MapPinIcons;
        const textColor = isVirtual ? 'primary.main' : 'success.main';

        const content =
          isVirtual && href ? (
            <Link
              href={href}
              target='_blank'
              rel='noopener noreferrer'
              underline='hover'
              title={text}
              sx={{
                ml: 0.5,
                display: 'inline-block',
                maxWidth: 150,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'inherit',
              }}
            >
              {text}
            </Link>
          ) : (
            <Typography
              component='span'
              variant='body2'
              title={text}
              sx={{
                ml: 0.5,
                display: 'inline-block',
                maxWidth: 150,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {text || 'No Link'}
            </Typography>
          );

        return (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: '0.875rem',
              color: textColor,
            }}
          >
            <Box component='span' sx={{ mr: 0.5, display: 'inline-flex' }}>
              <Icon />
            </Box>
            <Box
              component='span'
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              {label}
              {content}
            </Box>
          </Box>
        );
      },
      disableSort: true,
    },

    {
      key: 'interviewStartTime',
      label: t(SI.candidates.columns.dateTime),
      width: '120px',
      align: 'left' as const,
      render: (row: ICandidate) => {
        if (!row.interviewStartTime) {
          return (
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              No Date Scheduled
            </Typography>
          );
        }
        const date = new Date(Number(row.interviewStartTime) * 1000);
        const formatted = date.toLocaleString('en-US', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
        return <Typography variant='body2'>{formatted}</Typography>;
      },
      disableSort: true,
    },
    {
      key: 'interviewApplicantStatus',
      label: t(SI.candidates.columns.applicantStatus),
      width: '100px',
      align: 'left' as const,
      render: (row: ICandidate) => (
        <StatusTracker status={row.interviewApplicantStatus} />
      ),
      disableSort: true,
    },
    {
      key: 'PenalistNotes',
      label: t(SI.candidates.columns.notes),
      width: '80px',
      align: 'left' as const,
      render: (row: ICandidate) => (
        <IconButton
          size='small'
          onClick={
            onOpenPanelistNotes ? () => onOpenPanelistNotes(row) : undefined
          }
          sx={{
            p: 1,
            bgcolor: row.hasPanelistNotes ? 'success.light' : 'action.hover',
            color: row.hasPanelistNotes ? 'success.dark' : 'text.secondary',
            '&:hover': {
              bgcolor: row.hasPanelistNotes ? 'success.main' : 'action.selected',
              color: row.hasPanelistNotes ? 'success.contrastText' : 'text.primary',
            },
          }}
          title={
            row.hasPanelistNotes
              ? 'View Panelist notes'
              : 'There is no Panelist notes yet'
          }
          disabled={!onOpenPanelistNotes}
        >
          <NotebookIcons />
        </IconButton>
      ),
      disableSort: true,
    },
    {
      key: 'record',
      label: '',
      width: '40px',
      align: 'left' as const,
      render: (row: ICandidate) => {
        const hasAddress = !!row.inPersonAddress?.trim();
        const isRecording = row.id === recordingRowId;

        if (!hasAddress) {
          return null;
        }

        return (
          <IconButton
            size='small'
            onClick={onRecordInterview ? () => onRecordInterview(row) : undefined}
            sx={{
              p: 1,
              bgcolor: isRecording ? 'error.light' : 'action.hover',
              color: isRecording ? 'error.dark' : 'text.secondary',
              '&:hover': {
                bgcolor: isRecording ? 'error.main' : 'action.selected',
                color: isRecording ? 'error.contrastText' : 'text.primary',
              },
            }}
            title={isRecording ? 'Stop Recording' : 'Start Recording'}
            disabled={!onRecordInterview}
          >
            <MicIcons />
          </IconButton>
        );
      },
      disableSort: true,
    },

    {
      key: 'actions',
      label: '',
      width: '40px',
      align: 'right' as const,
      render: (row: ICandidate) => (
        <ContextMenuButton
          row={row}
          onOpenMenu={(e, r) => handleOpenMenu(e, r as ICandidate)}
          onCloseMenu={handleCloseMenu}
          anchorEl={anchorEl}
        />
      ),
      disableSort: true,
    },
  ];
};
